// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PurchasedProductCard from "@insite/content-library/Components/PurchasedProductCard";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import CardListHeading, { CardListHeadingStyles } from "@store/Overrides/Components/CardListHeading";
import { tableProps } from "@store/Themes/common/props/table";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import cssProp from "@store/Framework/Common/Utilities/cssProp";
import { useContext, useMemo } from "react";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
/* Starter Custom Code - End */

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

/* Starter Custom Code - Start */
const titlePositionField = getTextAlignmentField(fields.titlePosition);
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
    // Starter Custom Code
    noResultsFoundText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    cardListHeading?: CardListHeadingStyles;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    itemHeader?: DataTableHeaderProps;
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    tableWrapper: tableProps.wrapper,
    dataTable: tableProps.dataTable,
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

const PurchasedProducts = ({ id, fields, products, canViewPurchasedProducts, loadPurchasedProducts }: Props) => {
    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({ widgetId: id, purchaseType: fields.purchaseType });
        }
    }, [fields.purchaseType]);

    /* Starter Custom Code - Start */
    const { isEditing, isInShell } = useContext(ShellContext);

    const cardListHeadingStyles = useMemo(() => {
        return mergeProps<PurchasedProductsStyles["cardListHeading"]>(styles.cardListHeading, {
            heading: {
                extendedStyles: {
                    title: {
                        css: css`
                            ${cssProp("text-align", fields.titlePosition)}
                        `,
                    },
                },
            },
        });
    }, [styles.cardListHeading, fields.titlePosition]);

    if (canViewPurchasedProducts && isInShell && isEditing && (!products || !products?.length)) {
        return (
            <Typography {...styles.noResultsFoundText} as="p">
                {translate("No products found")}
            </Typography>
        );
    }
    /* Starter Custom Code - End */

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }

    return (
        /* Starter Custom Code - Start */
        <StyledWrapper {...styles.wrapper}>
            <CardListHeading heading={fields.title} extendedStyles={cardListHeadingStyles} />
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader {...styles.itemHeader}>{translate("Item")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        {products.map(product => (
                            <PurchasedProductCard
                                key={product.id}
                                product={product}
                                widgetId={id}
                                showBrand={fields.showBrand}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAvailability={fields.showAvailability}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList}
                            />
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
        /* Starter Custom Code - End */
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
                isRequired: true, // Starter Custom Code
            },
            /* Starter Custom Code - Start */
            {
                ...titlePositionField.definition,
                displayName: "Title Position",
                defaultValue: textAlignmentOptions.left.id,
                fieldType: "Contextual",
                sortOrder: 1,
            },
            /* Starter Custom Code - End */
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
        ],
    },
};

export default widgetModule;
